/**
 * Change signals for request data that several detached components render.
 *
 * Two problems, one shape. The sidebar badge fetches its Needs Action count
 * once on mount. `useOrderRovRequests` gives every caller its own private state
 * and its own refresh(), with no shared cache — so the order page runs three
 * independent copies (banners, Revisions tab, history card) that refresh only
 * themselves. Acting in one leaves the others showing what used to be true,
 * and since the banner and the history now sit in a single viewport, they
 * visibly disagree.
 *
 * Module-level subscriptions rather than Redux or prop chains: the publishers
 * and subscribers live in different trees (Sidebar is in layout, the banner is
 * above the tabs, the history is inside one), so the alternatives are wiring
 * several unconnected components to the store or threading callbacks through
 * layout. More machinery than a refetch signal deserves.
 *
 * Two topics rather than one, because they have different audiences: every ROV
 * mutation changes the queue AND the request lists, while a cancellation
 * changes only the queue.
 */

const createTopic = () => {
  const listeners = new Set()
  return {
    subscribe(listener) {
      listeners.add(listener)
      return () => listeners.delete(listener)
    },
    publish() {
      listeners.forEach((listener) => {
        try {
          listener()
        } catch {
          // One bad listener must not stop the rest, and none of this is worth
          // breaking the action over — it already succeeded.
        }
      })
    },
  }
}

const needsAction = createTopic()
const rovRequests = createTopic()

/**
 * The Needs Action queue changed: a request was filed, resolved or withdrawn.
 * @param {() => void} listener
 * @returns {() => void} unsubscribe
 */
export const onNeedsActionChanged = (listener) => needsAction.subscribe(listener)

/** Tell every listener the queue changed. Safe to call with none. */
export const notifyNeedsActionChanged = () => needsAction.publish()

/**
 * An order's pending revision/ROV requests changed. Every `useOrderRovRequests`
 * instance refetches, so detached views cannot disagree about the same order.
 * @param {() => void} listener
 * @returns {() => void} unsubscribe
 */
export const onRovRequestsChanged = (listener) => rovRequests.subscribe(listener)

/**
 * Tell every listener an ROV request changed.
 *
 * Also announces the queue change: filing, withdrawing and resolving all move
 * a request in or out of Needs Action, so a caller never has to remember both.
 */
export const notifyRovRequestsChanged = () => {
  rovRequests.publish()
  needsAction.publish()
}
